import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import ReactPlayer from 'react-player';
import React, { useEffect, useState } from 'react';
import { GetArtifactDetailResponse } from 'src/components/ArtifactDetail/ArtifactDetailResponse';
import { Tabs } from '@cloudscape-design/components';

type useRenderContentProps = {
    data: GetArtifactDetailResponse;
    artifactId: string;
    baseDynamicURL: string;
    setModalHref: (href: string) => void;
    loading: boolean;
};
export const useRenderContent = ({
    artifactId,
    baseDynamicURL,
    data,
    loading,
    setModalHref,
}: useRenderContentProps) => {
    const [artifactHtml, setArtifactHtml] = useState(parse(''));
    const [css, setCss] = useState<string>('');
    const [cssLoaded, setCssLoaded] = useState(false);
    useEffect(() => {
        if (loading) {
            setArtifactHtml(parse(''));
            setCss('');
        }
    }, [loading]);

    const findTabInformation = (child: any, dataTab = ''): any => {
        if (!child.children || !child.children.length) {
            return { child, dataTab };
        }

        const data_tab = child.attribs['data-tab'] || dataTab;
        const nextChoice = child.attribs.role === 'tab' ? 1 : 0;
        return findTabInformation(child.children[nextChoice], data_tab);
    };
    const options: HTMLReactParserOptions = {
        replace: (node: any) => {
            // This is all hidden or display none. Not sure why it's delivered
            if (node.name === 'script') {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                (node.attribs.class === 'FragmentSkinComponentWrapper' ||
                    node.attribs.class === 'LoadImgPlaceholder')
            ) {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('HideUntilLoad')
            ) {
                node.attribs.class = node.attribs.class.replace(
                    'HideUntilLoad',
                    ''
                );
                node.attribs.id = `${node.attribs.id}-1`;
            }
            if (
                node.attribs &&
                (node.attribs.class === 'Main' ||
                    node.attribs.class == 'OuterWrap')
            ) {
                node.attribs.class = 'Removed';
                node.attribs.id = `${node.attribs.id}-1`;
            }
            // TODO: Error if there's nothing for data-src
            if (
                node.name === 'video' &&
                node.attribs &&
                node.attribs['data-src']
            ) {
                const url = data.videoUrls.find(
                    (video: { src: string; data: any }) =>
                        video.src === node.attribs['data-src']
                );
                return <ReactPlayer url={url?.data} controls />;
            }

            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Xref')
            ) {
                const href = node.attribs.href;
                node.attribs.href = `/${artifactId}#/${href}`;
                return (
                    <a
                        className={node.attribs.class}
                        onClick={() => {
                            setModalHref(href);
                        }}
                    >
                        {node.children[0].data}
                    </a>
                );
            }

            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('InternalLink')
            ) {
                node.attribs.href = `${baseDynamicURL}${node.attribs.href}`;
            }

            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Tabs TabsComponent ')
            ) {
                const tabInformation = node.children[0].children.map(
                    (child: any) => {
                        return findTabInformation(child);
                    }
                );
                const tabs = tabInformation.map((tab: any, index: number) => {
                    const content = node.children[1].children[index];
                    return {
                        label: tab.child.data,
                        id: tab.dataTab,
                        content: domToReact(
                            content.children[1].children,
                            options
                        ),
                    };
                });
                return <Tabs tabs={tabs} />;
            }
        },
    };

    useEffect(() => {
        if (data) {
            if (data.media && data.media.length) {
                data.media.forEach((media: { src: string; data: string }) => {
                    // Images may include special characters like (). Need to escape those
                    // These escapes were LLM generated so if there are missing elements for special characters
                    // start here
                    const escapedSrc = media.src.replace(
                        /[.*+?^${}()|[\]\\]/g,
                        '\\$&'
                    );
                    const regex = new RegExp(escapedSrc, 'g');
                    const matches = [...data.html.matchAll(regex)];
                    matches.forEach((match) => {
                        data.html = data.html.replace(match[0], media.data);
                    });
                });
            }
            setCss(removeFontFaces(data.css));
            if (!data.css) {
                setCssLoaded(true);
            }
            setArtifactHtml(parse(data.html, options));
        }
    }, [data]);

    function removeFontFaces(cssText: string) {
        // This regex matches @font-face blocks, accounting for nested braces
        const fontFaceRegex = /@font-face\s*{[^{}]*({[^{}]*})*[^{}]*}/g;

        return cssText.replace(fontFaceRegex, '');
    }

    useEffect(() => {
        let linkElement: HTMLLinkElement | null = null;
        if (css) {
            const cssContent = `.xyleme-container {
                ${css}
            }`;
            const blob = new Blob([cssContent], { type: 'text/css' });
            const url = URL.createObjectURL(blob);

            linkElement = document.createElement('link');
            linkElement.rel = 'stylesheet';
            linkElement.href = url;
            document.head.appendChild(linkElement);

            setCssLoaded(true);
            return () => {
                if (linkElement) {
                    document.head.removeChild(linkElement);
                    URL.revokeObjectURL(url);
                }
            };
        }
    }, [css]);

    return { artifactHtml: cssLoaded ? artifactHtml : parse('') };
};
